import { useLayoutEffect, useState } from 'react'
import { paneLayout, type PaneLayout } from './inboxLayout'

/** The pane's layout from its measured width (ResizeObserver), as a callback
 * ref plus the layout. Not `useContainerWidth`: that hook observes once, keyed
 * on a ref OBJECT, and the Inbox detail renders a spinner or "Select a
 * conversation" before the pane exists (and the spinner again on every thread
 * switch), so the ref is null at mount and the pane would never be measured.
 * Holding the element in state re-runs the effect for each element attached.
 * The layout, not the width, is stored: React bails out on an unchanged value,
 * so the detail re-renders only when a resize crosses the threshold. 'stacked'
 * with no element. */
export function usePaneLayout(): [(el: HTMLDivElement | null) => void, PaneLayout] {
  const [el, setEl] = useState<HTMLDivElement | null>(null)
  const [layout, setLayout] = useState<PaneLayout>('stacked')
  useLayoutEffect(() => {
    if (!el) {
      setLayout('stacked')
      return
    }
    setLayout(paneLayout(el.getBoundingClientRect().width))
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (typeof w === 'number') setLayout(paneLayout(w))
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [el])
  return [setEl, layout]
}
